import styled from "styled-components"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import Matter, { Engine, Render, World, Bodies, Runner, Body, Events } from "matter-js"
import useDevice from "@/hooks/useDevice"
import { DEVICE_TYPES } from "@/constants"
import { useViewportWidth } from "@/hooks/useViewport"
import { getColoredSvg } from "@/utils/canvas"
import { VARIANTS } from "@/components/BannerCallToActionModule/index"
import { colors } from "@/styles/themeSettings"
import useIsFirefoxBrowser from "@/hooks/useIsFirefoxBrowser"
import WhiteCross from "@/assets/images/white_cross.png"
import BlueCross from "@/assets/images/blue_cross.png"

const StyledWrapper = styled.div`
	top: 0;
	z-index: 0;
	left: 0;
	position: absolute;
	height: 100%;
	width: 100%;
	//pointer-events: none;
`

const StyledCanvas = styled.canvas`
	width: 100%;
	height: 100%;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 2;
`

export const SVG_CANVAS_OPTIONS = {
	[DEVICE_TYPES.DESKTOP]: {
		MAX_BALLS_COUNT: 75,
		BALL_SIZE: 20,
		BALL_SCALE: 0.15,
	},
	[DEVICE_TYPES.TABLET]: {
		MAX_BALLS_COUNT: 50,
		BALL_SIZE: 15,
		BALL_SCALE: 0.13,
	},
	[DEVICE_TYPES.MOBILE]: {
		MAX_BALLS_COUNT: 30,
		BALL_SIZE: 13,
		BALL_SCALE: 0.12,
	},
}

export const PNG_CANVAS_OPTIONS = {
	[DEVICE_TYPES.DESKTOP]: {
		MAX_BALLS_COUNT: 100,
		BALL_SIZE: 20,
		BALL_SCALE: 0.21,
	},
	[DEVICE_TYPES.TABLET]: {
		MAX_BALLS_COUNT: 50,
		BALL_SIZE: 15,
		BALL_SCALE: 0.17,
	},
	[DEVICE_TYPES.MOBILE]: {
		MAX_BALLS_COUNT: 30,
		BALL_SIZE: 13,
		BALL_SCALE: 0.15,
	},
}

function getBgColorBasedOnVariant(variant) {
	switch (variant) {
		case VARIANTS.RED:
			return colors.richBlue
		case VARIANTS.NAVY_BLUE:
			return colors.white
		case VARIANTS.RED_WHITE_CROSSES:
			return colors.platinum
		default:
			return colors.platinum
	}
}

const BallSpawner = forwardRef(({ variant, bgColor }, ref) => {
	const vW = useViewportWidth()
	const { isDesktop, isTablet } = useDevice()
	const isFirefox = useIsFirefoxBrowser()
	const deviceType = isDesktop ? DEVICE_TYPES.DESKTOP : isTablet ? DEVICE_TYPES.TABLET : DEVICE_TYPES.MOBILE
	const canvasRef = useRef()
	const engineRef = useRef(Engine.create())
	const renderRef = useRef()
	const runnerRef = useRef()
	const ballsRef = useRef([])
	const cursorBallRef = useRef(null)
	const posXRef = useRef(null)
	const posYRef = useRef(null)
	const prevPosXRef = useRef(null)
	const prevPosYRef = useRef(null)
	const lastUpdateTimeRef = useRef(Date.now())
	const targetRadiusRef = useRef(20)
	const currentRadiusRef = useRef(20)

	const forceMagnitude = 0.1
	const crossColor = getBgColorBasedOnVariant(variant)
	const svgUrl = getColoredSvg(crossColor)
	const pngSrc = variant === VARIANTS.NAVY_BLUE ? WhiteCross.src : BlueCross.src

	const texture = isFirefox ? pngSrc : svgUrl
	const OPTIONS_SOURCE = isFirefox ? PNG_CANVAS_OPTIONS : SVG_CANVAS_OPTIONS

	const { MAX_BALLS_COUNT, BALL_SIZE, BALL_SCALE } = useMemo(() => OPTIONS_SOURCE[deviceType], [OPTIONS_SOURCE, deviceType])

	const INITIAL_BALLS_COUNT = MAX_BALLS_COUNT

	const [isFirstRender, setIsFirstRender] = useState(true)

	useEffect(() => {
		if (isFirstRender) return
		initializePhysics()

		return () => {
			if (isFirstRender) return
			clearPhysics()
		}
	}, [isFirstRender, vW, deviceType, isFirefox])

	useEffect(() => {
		setIsFirstRender(false)
	}, [])

	useImperativeHandle(ref, () => ({
		addBall: () => {
			addBall(canvasRef.current.offsetWidth - 30, 30, true)
		},
	}))

	const initializePhysics = useCallback(() => {
		if (!canvasRef.current) return

		const height = canvasRef.current.offsetHeight
		const width = canvasRef.current.offsetWidth

		const engine = engineRef.current || Engine.create()
		engineRef.current = engine

		const render = Render.create({
			element: canvasRef.current.parentElement,
			engine: engine,
			options: {
				width,
				height,
				wireframes: false,
				background: bgColor?.value,
				// background: 'transparent',
				pixelRatio: Math.min(window.devicePixelRatio, 2),
			},
		})
		renderRef.current = render

		const cursorBallMass = 10
		cursorBallRef.current = Bodies.circle(width / 2, height / 2, 20, {
			mass: cursorBallMass,
			friction: 0.001,
			restitution: 0.8,
			density: 1,
			isStatic: true,
			render: { fillStyle: "transparent" },
		})

		World.add(engine.world, cursorBallRef.current)

		createBoundaries(width, height)

		engine.gravity.y = 1.5

		runnerRef.current = Runner.create({ timestep: 1000 / 30, isFixed: true })
		Runner.run(runnerRef.current, engine)
		Render.run(render)

		addInitialBalls(width)
		setupBeforeUpdateEvent()
	}, [deviceType, isFirefox])

	const clearPhysics = () => {
		if (!engineRef.current) return

		const render = renderRef.current
		const runner = runnerRef.current
		const engine = engineRef.current

		if (render) Render.stop(render)
		if (runner) Runner.stop(runner)
		if (render && render.canvas) render.canvas.remove()

		World.clear(engine.world)
		Engine.clear(engine)

		renderRef.current = null
		runnerRef.current = null
		engineRef.current = null
		ballsRef.current = []
	}

	const createBoundaries = (width, height) => {
		World.add(engineRef.current.world, [
			Bodies.rectangle(width / 2, height + 25, width * 5, 50, { isStatic: true, background: "transparent" }), // top wall
			Bodies.rectangle(-50, height / 2, 100, height * 5, { isStatic: true }), // left wall
			Bodies.rectangle(width + 50, height / 2, 100, height * 5, { isStatic: true }), // right wall
			Bodies.rectangle(width / 2, -50, width * 5, 100, { isStatic: true }), // bottom wall
		])
	}

	const addInitialBalls = (width) => {
		let addedBalls = 0
		const batchSize = 20
		const interval = setInterval(() => {
			for (let i = 0; i < batchSize; i++) {
				if (addedBalls >= INITIAL_BALLS_COUNT) {
					clearInterval(interval)
					break
				}
				const { x, y } = getNewBallPosition(width)
				addBall(x, y, false)
				addedBalls = addedBalls + 1
			}
		}, 40)
	}

	const getNewBallPosition = (width) => {
		return {
			x: width - Math.random() * 50,
			y: Math.random() * 50,
		}
	}

	const checkBallsOutOfBounds = (ball, index) => {
		const width = canvasRef.current.offsetWidth
		const height = canvasRef.current.offsetHeight

		const { x, y } = ball.position

		if (x < -100 || x > width + 100 || y < -100 || y > height + 100) {
			World.remove(engineRef.current.world, ball)
			ballsRef.current.splice(index, 1)

			if (posXRef.current != null && posYRef.current != null) {
				addBall(posXRef.current, posYRef.current, false)
			}
		}
	}

	const applyForceToCloseBall = (ball, impactDistance) => {
		const distance = Matter.Vector.magnitude(Matter.Vector.sub(cursorBallRef.current.position, ball.position))

		if (distance < impactDistance) {
			const forceMagnitude = 0.05
			const forceDirection = Matter.Vector.normalise(Matter.Vector.sub(ball.position, cursorBallRef.current.position))
			Body.applyForce(ball, ball.position, Matter.Vector.mult(forceDirection, forceMagnitude))
		}
	}

	const setupBeforeUpdateEvent = () => {
		Events.off(engineRef.current, "beforeUpdate")
		Events.on(engineRef.current, "beforeUpdate", () => {
			if (posXRef.current != null && posYRef.current != null) {
				const cursorBall = cursorBallRef.current
				Body.setPosition(cursorBall, { x: posXRef.current, y: posYRef.current })

				currentRadiusRef.current = lerp(currentRadiusRef.current, targetRadiusRef.current, 0.04)
				Body.scale(
					cursorBall,
					currentRadiusRef.current / cursorBall.circleRadius,
					currentRadiusRef.current / cursorBall.circleRadius,
				)

				const impactDistance = currentRadiusRef.current

				ballsRef.current.forEach((ball, index) => {
					checkBallsOutOfBounds(ball, index)

					if (Matter.Vector.magnitude(Matter.Vector.sub(cursorBall.position, ball.position)) < impactDistance * 2) {
						applyForceToCloseBall(ball, impactDistance)
					}
				})
			}
		})
	}

	const handleMouseMove = useCallback(
		(event) => {
			if (!canvasRef.current || !cursorBallRef.current || !isDesktop) return

			const rect = canvasRef.current.getBoundingClientRect()
			posXRef.current = event.clientX - rect.x
			posYRef.current = event.clientY - rect.y

			const currentTime = Date.now()
			const timeDelta = (currentTime - lastUpdateTimeRef.current) / 1000

			if (prevPosXRef.current != null && prevPosYRef.current != null && timeDelta > 0) {
				const deltaX = posXRef.current - prevPosXRef.current
				const deltaY = posYRef.current - prevPosYRef.current
				const speed = Math.sqrt(deltaX ** 2 + deltaY ** 2) / timeDelta

				updateTargetRadius(speed)
			}

			prevPosXRef.current = posXRef.current
			prevPosYRef.current = posYRef.current
			lastUpdateTimeRef.current = currentTime
		},
		[isDesktop],
	)

	const getNormalizedSpeed = (speed) => {
		const minSpeed = 10
		const maxSpeed = 5000
		return Math.min(Math.max(speed - minSpeed, 0), maxSpeed - minSpeed) / (maxSpeed - minSpeed)
	}

	const updateTargetRadius = (speed) => {
		const scaleFactor = 0.8
		const normalizedSpeed = getNormalizedSpeed(speed)
		targetRadiusRef.current = 10 + normalizedSpeed * 300 * scaleFactor
	}

	const lerp = (start, end, alpha) => start + (end - start) * alpha

	const handleMouseLeave = () => {
		if (!isDesktop) return

		// Remove cursor ball from the world
		if (cursorBallRef.current) {
			World.remove(engineRef.current.world, cursorBallRef.current)
			cursorBallRef.current = null
		}
		// Reset cursor position references
		posXRef.current = null
		posYRef.current = null
		targetRadiusRef.current = 0
	}

	const handleMouseEnter = (event) => {
		if (!isDesktop) return

		const rect = canvasRef.current.getBoundingClientRect()
		const mouseX = event.clientX - rect.x
		const mouseY = event.clientY - rect.y

		// Recreate cursor ball at the mouse position
		cursorBallRef.current = Bodies.circle(mouseX, mouseY, 20, {
			mass: 10,
			friction: 0.001,
			restitution: 0.8,
			density: 1,
			isStatic: true,
			render: { fillStyle: "transparent" },
		})

		World.add(engineRef.current.world, cursorBallRef.current)

		// Update position references
		posXRef.current = mouseX
		posYRef.current = mouseY
		targetRadiusRef.current = 20
	}

	const addBall = (x, y, applyForce = true) => {
		if (!engineRef.current || !engineRef.current.world) {
			console.error("Physics engine not initialized")
			return
		}

		const newBall = Bodies.circle(x, y, BALL_SIZE, {
			render: {
				sprite: {
					texture,
					yScale: BALL_SCALE,
					xScale: BALL_SCALE,
				},
			},
		})

		if (applyForce) {
			Body.applyForce(newBall, { x: newBall.position.x, y: newBall.position.y }, { x: 0, y: forceMagnitude })
		}

		World.add(engineRef.current.world, newBall)
		ballsRef.current.push(newBall)

		if (ballsRef.current.length > MAX_BALLS_COUNT) {
			const oldestBall = ballsRef.current.shift()
			World.remove(engineRef.current.world, oldestBall)
		}
	}

	return (
		<StyledWrapper>
			<StyledCanvas ref={canvasRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter} />
		</StyledWrapper>
	)
})

BallSpawner.displayName = "BallSpawner"

export default BallSpawner
